'use client';
import { FC, ReactNode } from 'react';
import classNames from 'classnames';

import { useSiteSettings } from '@gn-audio/jss-components/utils/contexts';
import { AvailabilityState, ProductType, SegmentType } from '@gn-audio/jss-core';
import { PriceDisplayOption } from '@gn-audio/jss-core/site-settings/enums';

import { StandardPriceModel } from 'api/pricesApi/pricesModel';
import { SegmentTypes } from 'api/productCatalogueApi';
import { useFormat } from 'common/contexts/FormatContext';
import { getAvailabilityState } from 'composables/Product/Buy/customizerHelpers';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';
import { isNotNil } from 'utils/utils';

import styles from './Price.module.scss';

export interface PriceProps {
  productType: ProductType;
  segmentType?: SegmentType | undefined;
  price?: StandardPriceModel | null;
  className?: string;
}

export const Price: FC<PriceProps> = ({ price, productType, segmentType, className }) => {
  const { t } = useFallbackTranslation();
  const { formatCurrency } = useFormat();
  const { display } = useSiteSettings();

  const availability = getAvailabilityState(price);

  const isFamilyPriceWithoutAvailability =
    !!price?.familyId && price.availabilityState === undefined;

  const isNotAvailable =
    isNotNil(availability) &&
    availability >= AvailabilityState.WhereToBuy &&
    !isFamilyPriceWithoutAvailability &&
    !!price;

  const comingSoon = availability === AvailabilityState.ComingSoon;
  const isBusiness = segmentType === SegmentTypes.Business;
  const isConsumer = segmentType === SegmentTypes.Consumer;
  const thresholdPrice = display?.priceDisplayThreshold;
  const discountedPrice = price?.discountedPrice;

  const excludeVat =
    display?.priceDisplayOption === PriceDisplayOption.ExcludeVATThreshold &&
    (isBusiness || (thresholdPrice && discountedPrice && discountedPrice < thresholdPrice));

  const includeVat =
    (display?.priceDisplayOption === PriceDisplayOption.ExcludeIncludeVAT && isBusiness) ||
    (display?.priceDisplayOption === PriceDisplayOption.IncludesVATLabel &&
      (isBusiness || isConsumer));

  const isPriceDiscounted = price?.isDiscounted;

  const isFamily = productType === ProductType.Family;

  const renderMainPriceSection = (
    listedPrice?: number,
    discountedPrice?: number,
    discounted?: boolean
  ): ReactNode => {
    return (
      <span className="flex gap-3">
        <span className={classNames([discounted ? 'line-through' : 'font-bold'])}>
          {formatCurrency(listedPrice)}
        </span>

        {!!discounted && <span className="font-bold">{formatCurrency(discountedPrice)}</span>}
      </span>
    );
  };

  const renderLabel = (): ReactNode => {
    return isFamily ? <span className={styles.label}>{t('system.PriceFrom')}</span> : null;
  };

  if (isNotAvailable) {
    return (
      <span className={classNames(className, 'simpleBadge inline')}>
        {comingSoon ? t('system.PriceComingSoon') : t('system.SoldOut')}
      </span>
    );
  }

  return (
    <span className={className}>
      {price && (
        <span
          className={classNames(
            styles.priceContainer,

            isPriceDiscounted ? 'discounted group' : undefined
          )}
        >
          {excludeVat && (
            <span className="flex flex-col">
              <span className="text-[0.75rem] font-bold uppercase">{renderLabel()}</span>
              {renderMainPriceSection(
                price.listedPriceWithoutVat,
                price.discountedPriceWithoutVat,
                isPriceDiscounted
              )}
              <span className="text-[0.75rem]">
                <span>{t('system.ExcludingVAT')}</span>
              </span>
            </span>
          )}
          {includeVat && (
            <span className="flex flex-col">
              <span className="text-[0.75rem] font-bold uppercase">{renderLabel()}</span>
              {renderMainPriceSection(
                price.listedPriceWithoutVat,
                price.discountedPriceWithoutVat,
                isPriceDiscounted
              )}
              <span className="text-[0.75rem]">
                (
                <span>
                  {isPriceDiscounted
                    ? formatCurrency(price.discountedPrice)
                    : formatCurrency(price.listedPrice)}
                </span>
                <span>&nbsp;{t('system.IncludingVAT')}</span>)
              </span>
            </span>
          )}

          {!excludeVat && !includeVat && (
            <span className="flex gap-3">
              {renderLabel()}
              {renderMainPriceSection(price.listedPrice, price.discountedPrice, isPriceDiscounted)}
            </span>
          )}
        </span>
      )}
      {price === undefined && (
        <span
          className={classNames(
            styles.priceContainer,

            isPriceDiscounted ? 'discounted group' : undefined
          )}
        >
          <span className="flex flex-col" aria-hidden>
            <span className="text-[0.75rem] font-bold uppercase"></span>
            <span className="flex gap-3">
              <span className="animate-glow">x xxx,xx xx</span>
            </span>
            <span className="animate-thin-glow text-[0.75rem]">
              (<span>x xxx,xx xx</span>
              <span>&nbsp;x xxx xxx</span>)
            </span>
          </span>
        </span>
      )}
    </span>
  );
};
